import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import AccountHeroBit from '../../components/account/AccountHeroBit';
import WishlistGrid from '../../components/account/WishlistGrid';
import LuxuryButton from '../../components/ui/LuxuryButton';
import LuxuryLoading from '../../components/ui/LuxuryLoading';
import {
    FaHeart, FaArrowRight, FaLeaf, FaShoppingCart,
    FaFilter, FaSortAmountDown, FaTimes
} from 'react-icons/fa';
import { fadeInUp, pageTransition } from "../../components/animations/animations";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import { UserService } from "../../api/user.api";

const Wishlist = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [wishlistItems, setWishlistItems] = useState([]);
    const [filter, setFilter] = useState('all');
    const [sortBy, setSortBy] = useState('date');
    const [selectedItems, setSelectedItems] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        loadWishlist();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const loadWishlist = async () => {
        try {
            setLoading(true);
            const data = await UserService.getWishlist();
            setWishlistItems(data);
        } catch (err) {
            setError(err.message || 'Failed to load wishlist');
        } finally {
            setLoading(false);
        }
    };

    const handleRemove = async (productId) => {
        try {
            await UserService.removeFromWishlist(productId);
            setWishlistItems(prev => prev.filter(item => item._id !== productId));
            setSelectedItems(prev => prev.filter(id => id !== productId));
            setSuccess('Item removed from wishlist');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.message || 'Failed to remove item');
        }
    };

    const handleClearWishlist = async () => {
        if (!window.confirm('Are you sure you want to clear your entire wishlist?')) return;

        try {
            await UserService.clearWishlist();
            setWishlistItems([]);
            setSelectedItems([]);
            setSuccess('Wishlist cleared successfully');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.message || 'Failed to clear wishlist');
        }
    };

    const handleMoveToCart = async (productId) => {
        try {
            // Implement cart API call here
            // await CartService.addToCart(productId, 1);
            await handleRemove(productId);
            setSuccess('Item moved to cart');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.message || 'Failed to move item to cart');
        }
    };

    const handleSelectAll = () => {
        setSelectedItems(
            selectedItems.length === filteredItems.length
                ? []
                : filteredItems.map(item => item._id)
        );
    };

    const handleMoveSelectedToCart = async () => {
        if (!selectedItems.length) return;

        try {
            // Implement batch add to cart
            // await Promise.all(selectedItems.map(id => CartService.addToCart(id, 1)));

            // Remove selected items from wishlist
            await Promise.all(selectedItems.map(id => UserService.removeFromWishlist(id)));

            setWishlistItems(prev => prev.filter(item => !selectedItems.includes(item._id)));
            setSelectedItems([]);
            setSuccess(`${selectedItems.length} items moved to cart`);
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.message || 'Failed to move items to cart');
        }
    };

    const handleRemoveSelected = async () => {
        if (!selectedItems.length) return;

        if (!window.confirm(`Remove ${selectedItems.length} items from wishlist?`)) return;

        try {
            await Promise.all(selectedItems.map(id => UserService.removeFromWishlist(id)));
            setWishlistItems(prev => prev.filter(item => !selectedItems.includes(item._id)));
            setSelectedItems([]);
            setSuccess(`${selectedItems.length} items removed`);
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.message || 'Failed to remove items');
        }
    };

    // Filter and sort items
    const filteredItems = wishlistItems.filter(item => {
        if (filter === 'in-stock') return item.inStock;
        if (filter === 'out-of-stock') return !item.inStock;
        return true;
    });

    const sortedItems = [...filteredItems].sort((a, b) => {
        if (sortBy === 'price-high') return b.price - a.price;
        if (sortBy === 'price-low') return a.price - b.price;
        if (sortBy === 'rating') return b.rating - a.rating;
        if (sortBy === 'name') return a.name.localeCompare(b.name);
        return new Date(b.addedAt || b.createdAt) - new Date(a.addedAt || a.createdAt);
    });

    const totalValue = sortedItems.reduce((sum, item) => sum + (item.price || 0), 0);
    const inStockCount = wishlistItems.filter(item => item.inStock).length;

    if (loading) {
        return <LuxuryLoading message="Loading your wishlist..." />;
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-pearl to-nude/30 overflow-x-hidden">
            {/* Navbar */}
            <div
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                    isScrolled
                        ? 'bg-white/95 backdrop-blur-sm shadow-soft'
                        : 'bg-transparent'
                }`}
            >
                <Navbar/>
            </div>

            <motion.div
                variants={pageTransition}
                initial="initial"
                animate="animate"
                exit="exit"
                className="min-h-screen bg-gradient-to-b from-pearl to-nude/30 py-12 pt-28 pb-16"
            >
                <div className="container-luxury max-w-7xl mx-auto px-4">
                    {/* Notifications */}
                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                className="mb-6 p-4 bg-error/10 border border-error/20 rounded-2xl flex items-center justify-between"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-error/20 flex items-center justify-center">
                                        <FaTimes className="w-4 h-4 text-error" />
                                    </div>
                                    <p className="text-error text-sm">{error}</p>
                                </div>
                                <button
                                    onClick={() => setError(null)}
                                    className="text-charcoal/50 hover:text-charcoal"
                                >
                                    ✕
                                </button>
                            </motion.div>
                        )}

                        {success && (
                            <motion.div
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                className="mb-6 p-4 bg-sage/10 border border-sage/20 rounded-2xl flex items-center justify-between"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-sage/20 flex items-center justify-center">
                                        <FaLeaf className="w-4 h-4 text-sage" />
                                    </div>
                                    <p className="text-sage text-sm">{success}</p>
                                </div>
                                <button
                                    onClick={() => setSuccess(null)}
                                    className="text-charcoal/50 hover:text-charcoal"
                                >
                                    ✕
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <AccountHeroBit
                        title="Your Wishlist"
                        subtitle="Curated beauty essentials, waiting for you"
                        background="sage"
                    >
                        <motion.div variants={fadeInUp} className="flex flex-wrap gap-4 mt-6">
                            <div className="flex items-center gap-2 px-4 py-2 bg-white/50 rounded-full">
                                <FaHeart className="text-champagne"/>
                                <span>{wishlistItems.length} items • ${totalValue.toFixed(2)}</span>
                            </div>
                            <div className="flex items-center gap-2 px-4 py-2 bg-white/50 rounded-full">
                                <FaLeaf className="text-sage"/>
                                <span>{inStockCount} in stock</span>
                            </div>
                        </motion.div>
                    </AccountHeroBit>

                    {/* Controls */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                        <div className="flex items-center gap-3">
                            <LuxuryButton
                                variant="outline"
                                size="small"
                                onClick={() => setShowFilters(!showFilters)}
                                icon={<FaFilter />}
                            >
                                Filter & Sort
                            </LuxuryButton>

                            {selectedItems.length > 0 && (
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-charcoal/50">
                                        {selectedItems.length} selected
                                    </span>
                                    <LuxuryButton
                                        variant="secondary"
                                        size="small"
                                        onClick={handleMoveSelectedToCart}
                                        icon={<FaShoppingCart />}
                                    >
                                        Move to Cart
                                    </LuxuryButton>
                                    <LuxuryButton
                                        variant="ghost"
                                        size="small"
                                        onClick={handleRemoveSelected}
                                        icon={<FaTimes />}
                                    >
                                        Remove
                                    </LuxuryButton>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center gap-3">
                            <button
                                onClick={handleSelectAll}
                                className="text-sm text-champagne hover:text-champagne/80"
                            >
                                {selectedItems.length === sortedItems.length ? 'Deselect All' : 'Select All'}
                            </button>

                            {wishlistItems.length > 0 && (
                                <LuxuryButton
                                    variant="outline"
                                    size="small"
                                    onClick={handleClearWishlist}
                                >
                                    Clear All
                                </LuxuryButton>
                            )}
                        </div>
                    </div>

                    {/* Filters Panel */}
                    <AnimatePresence>
                        {showFilters && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mb-6 luxury-card overflow-hidden"
                            >
                                <div className="p-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <h4 className="font-medium text-charcoal mb-3 flex items-center gap-2">
                                                <FaFilter className="w-4 h-4" />
                                                Filter
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {[
                                                    { value: 'all', label: 'All Items' },
                                                    { value: 'in-stock', label: 'In Stock' },
                                                    { value: 'out-of-stock', label: 'Out of Stock' }
                                                ].map(option => (
                                                    <button
                                                        key={option.value}
                                                        onClick={() => setFilter(option.value)}
                                                        className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                                                            filter === option.value
                                                                ? 'bg-champagne text-white'
                                                                : 'bg-nude/20 text-charcoal/60 hover:bg-nude/30'
                                                        }`}
                                                    >
                                                        {option.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <h4 className="font-medium text-charcoal mb-3 flex items-center gap-2">
                                                <FaSortAmountDown className="w-4 h-4" />
                                                Sort By
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {[
                                                    { value: 'date', label: 'Recently Added' },
                                                    { value: 'price-high', label: 'Price: High to Low' },
                                                    { value: 'price-low', label: 'Price: Low to High' },
                                                    { value: 'rating', label: 'Highest Rated' },
                                                    { value: 'name', label: 'Name A-Z' }
                                                ].map(option => (
                                                    <button
                                                        key={option.value}
                                                        onClick={() => setSortBy(option.value)}
                                                        className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                                                            sortBy === option.value
                                                                ? 'bg-sage text-white'
                                                                : 'bg-nude/20 text-charcoal/60 hover:bg-nude/30'
                                                        }`}
                                                    >
                                                        {option.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence>
                        {sortedItems.length ? (
                            <WishlistGrid
                                items={sortedItems}
                                selectedItems={selectedItems}
                                onSelect={setSelectedItems}
                                onRemove={handleRemove}
                                onMoveToCart={handleMoveToCart}
                            />
                        ) : (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="text-center py-16 luxury-card"
                            >
                                <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-nude/30 flex items-center justify-center">
                                    <FaHeart className="w-12 h-12 text-charcoal/30" />
                                </div>
                                <h2 className="text-3xl font-medium text-charcoal mb-4">
                                    Your wishlist is empty
                                </h2>
                                <p className="text-charcoal/60 mb-8 max-w-md mx-auto">
                                    Save your favorite products here to purchase them later or keep track of items you love.
                                </p>
                                <Link to="/shop">
                                    <LuxuryButton
                                        variant="primary"
                                        size="large"
                                        icon={FaArrowRight}
                                        iconPosition="right"
                                    >
                                        Explore Collection
                                    </LuxuryButton>
                                </Link>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>
            {/* Footer */}
            <Footer/>
        </div>
    );
};

export default Wishlist;